import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Drawer,
  Avatar,
  Divider,
  Zoom,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Skeleton,
} from '@mui/material';
import {
  Close as CloseIcon,
  MenuOpen as MenuOpenIcon,
  Menu as MenuIcon,
  Favorite as HeartIcon,
  TrendingUp as TrendingIcon,
  Map as MapIcon,
  ViewList as ListIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getCategoryColor } from '../../theme/theme';

// Helper to convert hex to rgba
const hexToRgba = (hex, opacity = 1) => {
  // Default color if hex is invalid
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return `rgba(160, 174, 192, ${opacity})`; // Default grey
  }
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } catch (e) {
    return `rgba(160, 174, 192, ${opacity})`;
  }
};

// Custom marker icons based on category
const createCategoryIcon = (category, isSelected = false) => {
  const color = getCategoryColor(category);
  const size = isSelected ? 44 : 36;
  const colorLight = hexToRgba(color, 0.8);
  const colorShadow = hexToRgba(color, 0.4);

  return L.divIcon({
    className: 'custom-category-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: linear-gradient(135deg, ${color} 0%, ${colorLight} 100%);
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 15px ${colorShadow}, 0 2px 6px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        ${isSelected ? 'animation: pulse 1.5s infinite;' : ''}
      ">
        <span style="
          transform: rotate(45deg);
          color: white;
          font-size: ${isSelected ? '18px' : '14px'};
        ">🙏</span>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

// Origin marker (where prayer was created)
const originIcon = L.divIcon({
  className: 'origin-marker',
  html: `
    <div style="
      width: 20px;
      height: 20px;
      background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 8px rgba(76, 175, 80, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <span style="color: white; font-size: 10px;">✨</span>
    </div>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Custom cluster icon
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = Math.min(60, 40 + Math.log10(count) * 10);

  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: linear-gradient(135deg, #2C5282 0%, #1a365d 100%);
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 4px 15px rgba(44, 82, 130, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: ${size > 50 ? '16px' : '14px'};
      ">
        ${count}
      </div>
    `,
    className: 'custom-cluster-marker',
    iconSize: [size, size],
  });
};

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return date.toLocaleDateString();
};

// Format full date
const formatFullDate = (dateString) => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Parse geo location from various formats
const parseGeoLocation = (geoLocation) => {
  if (!geoLocation) return null;

  try {
    let lat, lng;

    if (typeof geoLocation === 'string') {
      const parsed = JSON.parse(geoLocation);
      if (parsed.coordinates && Array.isArray(parsed.coordinates)) {
        lng = parsed.coordinates[0];
        lat = parsed.coordinates[1];
      } else if (parsed.lat !== undefined && parsed.lng !== undefined) {
        lat = parsed.lat;
        lng = parsed.lng;
      }
    } else if (geoLocation.coordinates && Array.isArray(geoLocation.coordinates)) {
      lng = geoLocation.coordinates[0];
      lat = geoLocation.coordinates[1];
    } else if (Array.isArray(geoLocation) && geoLocation.length >= 2) {
      lng = geoLocation[0];
      lat = geoLocation[1];
    } else if (geoLocation.lat !== undefined && geoLocation.lng !== undefined) {
      lat = geoLocation.lat;
      lng = geoLocation.lng;
    }

    // Validate coordinates are valid numbers
    if (typeof lat === 'number' && typeof lng === 'number' &&
        !isNaN(lat) && !isNaN(lng) &&
        lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  } catch (e) {
    console.error('Error parsing geo location:', e, geoLocation);
  }
  return null;
};

// Map controller component
function MapController({ center, zoom, selectedPrayer }) {
  const map = useMap();

  useEffect(() => {
    if (selectedPrayer) {
      const coords = parseGeoLocation(selectedPrayer.geo_location);
      if (coords) {
        map.flyTo([coords.lat, coords.lng], 14, {
          duration: 1.5,
          easeLinearity: 0.25,
        });
      }
    }
  }, [selectedPrayer, map]);

  return null;
}

// Statistics Card Component
function StatsCard({ icon: Icon, label, value, color, delay = 0 }) {
  return (
    <Zoom in timeout={500} style={{ transitionDelay: `${delay}ms` }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          background: (theme) => alpha(theme.palette.background.paper, 0.9),
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          minWidth: 140,
        }}
      >
        <Avatar
          sx={{
            bgcolor: alpha(color, 0.15),
            color: color,
            width: 40,
            height: 40,
          }}
        >
          <Icon fontSize="small" />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight="700" color="text.primary">
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Paper>
    </Zoom>
  );
}

// Prayer List Item - Bootstrap Minimal Style
function PrayerListItem({ prayer, isSelected, onClick }) {
  const categoryColor = getCategoryColor(prayer.category || 'General');

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: isSelected ? 'action.selected' : 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* Content */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          mb: 1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {prayer.content}
      </Typography>

      {/* Meta row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {prayer.is_anonymous ? 'Anonymous' : (prayer.author?.username || 'Unknown')}
        </Typography>
        <Typography variant="caption" sx={{ color: categoryColor, fontWeight: 500 }}>
          {prayer.category || 'General'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', ml: 'auto' }}>
          {formatRelativeTime(prayer.collected_at)}
        </Typography>
      </Box>
    </Box>
  );
}

// Main Component
function CollectedPrayersMap({
  prayers = [],
  loading = false,
  open = false,
  onClose,
  title = "My Collected Prayers"
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mapRef = useRef(null);

  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'list'
  const [detailOpen, setDetailOpen] = useState(false);

  // Calculate statistics
  const stats = useMemo(() => {
    const categories = {};
    prayers.forEach(p => {
      const cat = p.category || 'General';
      categories[cat] = (categories[cat] || 0) + 1;
    });

    const topCategory = Object.entries(categories)
      .sort((a, b) => b[1] - a[1])[0];

    return {
      total: prayers.length,
      categories: Object.keys(categories).length,
      topCategory: topCategory ? topCategory[0] : 'None',
      thisMonth: prayers.filter(p => {
        const date = new Date(p.collected_at);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }).length,
    };
  }, [prayers]);

  // Calculate map center from prayers
  const mapCenter = useMemo(() => {
    if (prayers.length === 0) return [41.9028, 12.4964]; // Rome default

    const validCoords = prayers
      .map(p => parseGeoLocation(p.geo_location))
      .filter(Boolean);

    if (validCoords.length === 0) return [41.9028, 12.4964];

    const avgLat = validCoords.reduce((sum, c) => sum + c.lat, 0) / validCoords.length;
    const avgLng = validCoords.reduce((sum, c) => sum + c.lng, 0) / validCoords.length;

    return [avgLat, avgLng];
  }, [prayers]);

  const handlePrayerClick = (prayer) => {
    if (selectedPrayer?.id === prayer.id) {
      setSelectedPrayer(null);
      setTimeout(() => {
        setSelectedPrayer(prayer);
        setDetailOpen(true);
      }, 10);
    } else {
      setSelectedPrayer(prayer);
      setDetailOpen(true);
    }
  };

  const handleMarkerClick = (prayer) => {
    setSelectedPrayer(prayer);
    setDetailOpen(true);
  };

  const drawerWidth = 380;

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        bgcolor: 'background.default',
      }}
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1400,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderRadius: 0,
          background: (theme) => alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
        }}
      >
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="700">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {stats.total} prayers collected
          </Typography>
        </Box>

        {/* View Toggle */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: 'action.hover',
            borderRadius: 2,
            p: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setViewMode('map')}
            sx={{
              bgcolor: viewMode === 'map' ? 'background.paper' : 'transparent',
              boxShadow: viewMode === 'map' ? 1 : 0,
            }}
          >
            <MapIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode('list')}
            sx={{
              bgcolor: viewMode === 'list' ? 'background.paper' : 'transparent',
              boxShadow: viewMode === 'list' ? 1 : 0,
            }}
          >
            <ListIcon fontSize="small" />
          </IconButton>
        </Box>

        {!isMobile && viewMode === 'map' && (
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            {drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        )}
      </Paper>

      {/* Statistics Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 100,
          left: drawerOpen && !isMobile && viewMode === 'map' ? drawerWidth + 16 : 16,
          zIndex: 1350,
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          transition: 'left 0.3s ease',
        }}
      >
        <StatsCard
          icon={HeartIcon}
          label="Total"
          value={stats.total}
          color="#E53E3E"
          delay={0}
        />
        <StatsCard
          icon={TrendingIcon}
          label="This Month"
          value={stats.thisMonth}
          color="#38A169"
          delay={100}
        />
        <StatsCard
          icon={LayersIcon}
          label="Categories"
          value={stats.categories}
          color="#3182CE"
          delay={200}
        />
      </Box>

      {/* Sidebar Drawer */}
      {viewMode === 'map' && (
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 64,
              height: 'calc(100% - 64px)',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Sidebar Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="subtitle1" fontWeight="600">
                Collected Prayers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {prayers.length} {prayers.length === 1 ? 'prayer' : 'prayers'} in your collection
              </Typography>
            </Box>

            {/* Prayer List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={70}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Box>
              ) : prayers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                  <Typography color="text.secondary" gutterBottom>
                    No prayers collected yet
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Start collecting prayers from the dashboard
                  </Typography>
                </Box>
              ) : (
                prayers.map((prayer) => (
                  <PrayerListItem
                    key={prayer.id}
                    prayer={prayer}
                    isSelected={selectedPrayer?.id === prayer.id}
                    onClick={() => handlePrayerClick(prayer)}
                  />
                ))
              )}
            </Box>
          </Box>
        </Drawer>
      )}

      {/* Map Container */}
      {viewMode === 'map' && (
        <Box
          sx={{
            position: 'absolute',
            top: 64,
            left: drawerOpen && !isMobile ? drawerWidth : 0,
            right: 0,
            bottom: 0,
            transition: 'left 0.3s ease',
          }}
        >
          <MapContainer
            ref={mapRef}
            center={mapCenter}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapController selectedPrayer={selectedPrayer} />

            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={createClusterIcon}
              maxClusterRadius={50}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
            >
              {prayers.map((prayer) => {
                const coords = parseGeoLocation(prayer.geo_location);
                if (!coords) return null;

                const isSelected = selectedPrayer?.id === prayer.id;

                return (
                  <Marker
                    key={prayer.id}
                    position={[coords.lat, coords.lng]}
                    icon={createCategoryIcon(prayer.category || 'General', isSelected)}
                    eventHandlers={{
                      click: () => handleMarkerClick(prayer),
                    }}
                  >
                    <Popup>
                      <Box sx={{ minWidth: 200, p: 1 }}>
                        <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                          {prayer.is_anonymous ? 'Anonymous' : prayer.author?.username}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                          "{prayer.content?.substring(0, 100)}..."
                        </Typography>
                        <Chip
                          label={prayer.category || 'General'}
                          size="small"
                          sx={{ bgcolor: alpha(getCategoryColor(prayer.category), 0.15) }}
                        />
                      </Box>
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          </MapContainer>
        </Box>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Box
          sx={{
            position: 'absolute',
            top: 64,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'auto',
            p: 3,
            bgcolor: 'background.default',
          }}
        >
          <Paper
            elevation={1}
            sx={{
              maxWidth: 800,
              mx: 'auto',
              overflow: 'hidden',
            }}
          >
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    height={70}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            ) : prayers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No prayers collected yet
                </Typography>
                <Typography color="text.disabled">
                  Start collecting prayers to see them here
                </Typography>
              </Box>
            ) : (
              prayers.map((prayer) => (
                <PrayerListItem
                  key={prayer.id}
                  prayer={prayer}
                  isSelected={selectedPrayer?.id === prayer.id}
                  onClick={() => handlePrayerClick(prayer)}
                />
              ))
            )}
          </Paper>
        </Box>
      )}

      {/* Prayer Detail Sheet */}
      <Drawer
        anchor="bottom"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        sx={{ zIndex: 1500 }}
        PaperProps={{
          sx: {
            borderRadius: '24px 24px 0 0',
            maxHeight: '80vh',
          },
        }}
      >
        {selectedPrayer && (
          <PrayerDetailSheet
            prayer={selectedPrayer}
            onClose={() => setDetailOpen(false)}
          />
        )}
      </Drawer>
    </Box>
  );
}

// Prayer Detail Sheet Component - Elegant & Minimal
function PrayerDetailSheet({ prayer, onClose }) {
  const coords = parseGeoLocation(prayer.geo_location);
  const categoryColor = getCategoryColor(prayer.category || 'General');

  return (
    <Box sx={{ pb: 4 }}>
      {/* Handle */}
      <Box
        sx={{
          width: 32,
          height: 4,
          bgcolor: 'divider',
          borderRadius: 2,
          mx: 'auto',
          mt: 1.5,
          mb: 2,
        }}
      />

      {/* Close button - top right */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          bgcolor: 'action.hover',
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Category indicator */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.5,
            borderRadius: 3,
            bgcolor: alpha(categoryColor, 0.1),
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: categoryColor,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: categoryColor,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            {prayer.category || 'General'}
          </Typography>
        </Box>
      </Box>

      {/* Prayer Content - Hero */}
      <Box sx={{ px: 4, mb: 3 }}>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontStyle: 'italic',
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            lineHeight: 1.8,
            color: 'text.primary',
            textAlign: 'center',
          }}
        >
          "{prayer.content}"
        </Typography>
      </Box>

      {/* Author */}
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          mb: 3,
        }}
      >
        — {prayer.is_anonymous ? 'Anonymous' : (prayer.author?.username || 'Unknown')}
      </Typography>

      {/* Divider */}
      <Divider sx={{ mx: 4, mb: 3 }} />

      {/* Meta Info - Clean grid */}
      <Box
        sx={{
          px: 4,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ color: 'text.disabled', fontSize: '0.65rem', letterSpacing: 1 }}
          >
            Collected
          </Typography>
          <Typography variant="body2" fontWeight="500" color="text.primary">
            {formatRelativeTime(prayer.collected_at)}
          </Typography>
        </Box>

        {coords && coords.lat != null && coords.lng != null && (
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'text.disabled', fontSize: '0.65rem', letterSpacing: 1 }}
            >
              Location
            </Typography>
            <Typography variant="body2" fontWeight="500" color="text.primary">
              {coords.lat.toFixed(4)}°, {coords.lng.toFixed(4)}°
            </Typography>
          </Box>
        )}

        {prayer.created_at && (
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'text.disabled', fontSize: '0.65rem', letterSpacing: 1 }}
            >
              Prayer Date
            </Typography>
            <Typography variant="body2" fontWeight="500" color="text.primary">
              {new Date(prayer.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        )}

        {prayer.author?.username && !prayer.is_anonymous && (
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'text.disabled', fontSize: '0.65rem', letterSpacing: 1 }}
            >
              From
            </Typography>
            <Typography variant="body2" fontWeight="500" color="text.primary">
              {prayer.author.username}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CollectedPrayersMap;
